import { useEffect, useRef, useState, type ReactNode } from "react";
import type { MouseEvent } from "react";
import { createPortal } from "react-dom";
import { Check, Copy } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import {
  TruncatedText,
  truncatedTextClassName,
  truncatedTextContent,
  type TruncatedTextEllipsis,
} from "@/components/ui";
import { cn } from "@/lib/utils";
import { useAnchoredPanel } from "@/components/ui/overlay";

const COPIED_FEEDBACK_MS = 1800;

/** Safari fallback when `clipboard.writeText` is denied. Avoids lib.dom's deprecated `Document.execCommand` typing. */
function copySelectedTextLegacy(): boolean {
  const execCommand = Reflect.get(document, "execCommand");
  if (typeof execCommand !== "function") {
    return false;
  }
  return Boolean(execCommand.call(document, "copy"));
}

async function copyTextToClipboard(value: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    // Safari often denies writeText; use the legacy path below.
  }

  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.top = "0";
  textarea.style.left = "0";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  textarea.setSelectionRange(0, value.length);

  try {
    return copySelectedTextLegacy();
  } finally {
    document.body.removeChild(textarea);
  }
}

export interface ClickToCopyIdProps {
  value: string;
  /** Shown in the chip; full `value` is always copied. */
  displayValue?: ReactNode;
  className?: string;
  title?: string;
  /** Where the ellipsis appears on overflow. `start` shows the last characters. */
  ellipsis?: TruncatedTextEllipsis;
}

export function ClickToCopyId({
  value,
  displayValue,
  className,
  title,
  ellipsis = "end",
}: ClickToCopyIdProps) {
  const { t } = useTranslation("common");
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);

  const hideCopied = () => setCopied(false);

  const { panelStyle } = useAnchoredPanel({
    isOpen: copied,
    onClose: hideCopied,
    triggerRef,
    panelRef,
    placement: "above",
    align: "center",
    gap: 8,
    passive: true,
    closeOnOutsideClick: false,
  });

  useEffect(() => {
    if (!copied) {
      return;
    }
    const timeoutId = window.setTimeout(() => {
      setCopied(false);
    }, COPIED_FEEDBACK_MS);
    return () => window.clearTimeout(timeoutId);
  }, [copied]);

  if (!value) {
    return null;
  }

  const shown = displayValue ?? value;
  const buttonTitle =
    title ??
    (typeof shown !== "string" ? value : t("clickToCopy"));

  const handleClick = async (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();
    if (copied) {
      return;
    }
    const ok = await copyTextToClipboard(value);
    if (ok) {
      setCopied(true);
      return;
    }
    toast.error(t("copyFailed"));
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={(event) => void handleClick(event)}
        title={buttonTitle}
        aria-label={copied ? t("copied") : t("clickToCopyValue", { value })}
        className={cn(
          "group inline-flex min-w-0 max-w-full items-center gap-1 rounded-md border px-1.5 py-0.5 text-left font-mono text-sm transition-colors",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1",
          copied
            ? "border-success-400 bg-success-50 text-success-800"
            : "border-surface-200 bg-surface-50 text-surface-700 hover:border-surface-400 hover:bg-surface-100 hover:text-surface-900",
          className,
        )}
      >
        {typeof shown === "string" ? (
          <TruncatedText title={value} ellipsis={ellipsis}>
            {shown}
          </TruncatedText>
        ) : (
          <span className={truncatedTextClassName(ellipsis)} title={value}>
            {truncatedTextContent(shown, ellipsis)}
          </span>
        )}
        {copied ? (
          <Check
            className="h-3.5 w-3.5 shrink-0 text-success-700"
            aria-hidden
          />
        ) : (
          <Copy
            className="h-3.5 w-3.5 shrink-0 text-surface-400 transition-colors group-hover:text-surface-700"
            aria-hidden
          />
        )}
      </button>
      {copied
        ? createPortal(
            <div
              ref={panelRef}
              role="status"
              style={panelStyle}
              className="pointer-events-none z-[200] flex items-center gap-1.5 rounded-lg border border-surface-200 bg-surface-0 px-2.5 py-1.5 text-xs font-medium text-surface-800 shadow-lg"
            >
              <Check className="h-3.5 w-3.5 text-success-600" aria-hidden />
              {t("copied")}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
